import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, ExternalLink } from "lucide-react";
import Seo from "@/components/seo/Seo";

const API = process.env.REACT_APP_BACKEND_URL;
// Same-origin path so Swagger UI's title bar shows the user's own domain
// (atlasforgefinancial.com in production) rather than the internal API base.
const OPENAPI_URL = "/api/openapi.json";
const SWAGGER_CSS =
  "https://cdn.jsdelivr.net/npm/swagger-ui-dist@5.20.7/swagger-ui.css";
const SWAGGER_BUNDLE =
  "https://cdn.jsdelivr.net/npm/swagger-ui-dist@5.20.7/swagger-ui-bundle.js";
const SWAGGER_PRESET =
  "https://cdn.jsdelivr.net/npm/swagger-ui-dist@5.20.7/swagger-ui-standalone-preset.js";

/**
 * Branded Swagger UI mount for the auto-generated FastAPI OpenAPI 3.1 spec.
 * Loads the swagger-ui-dist bundle from a CDN and mounts into #swagger-ui.
 * Partners can also click "Import into Postman" to open the raw JSON.
 */
export default function ApiReference() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Idempotent script/style injection — safe across re-mounts.
    if (!document.querySelector('link[data-swagger-css]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = SWAGGER_CSS;
      link.dataset.swaggerCss = "true";
      document.head.appendChild(link);
    }

    const loadScript = (src, attr) =>
      new Promise((resolve) => {
        if (document.querySelector(`script[${attr}]`)) return resolve();
        const s = document.createElement("script");
        s.src = src;
        s.setAttribute(attr, "true");
        s.onload = resolve;
        document.body.appendChild(s);
      });

    (async () => {
      await loadScript(SWAGGER_BUNDLE, "data-swagger-bundle");
      await loadScript(SWAGGER_PRESET, "data-swagger-preset");
      if (window.SwaggerUIBundle) {
        window.SwaggerUIBundle({
          url: OPENAPI_URL,
          dom_id: "#swagger-ui",
          deepLinking: true,
          docExpansion: "list",
          filter: true,
          tryItOutEnabled: true,
          persistAuthorization: true,
          presets: [
            window.SwaggerUIBundle.presets.apis,
            window.SwaggerUIStandalonePreset.slice(1),
          ],
        });
        setReady(true);
      }
    })();
  }, []);

  return (
    <>
      <Seo
        title="API Reference"
        description="Interactive OpenAPI 3.1 reference for the AtlasForge Financial platform. Explore every endpoint, request schema, and response — with one-click Try it powered by the sandbox key."
        path="/developers/reference"
      />

      <section className="bg-[#0A1128] text-white pt-14 pb-10 px-6 lg:px-10 border-b border-white/5">
        <div className="mx-auto max-w-6xl">
          <Link
            to="/developers"
            data-testid="api-ref-back"
            className="inline-flex items-center gap-1.5 text-[13px] text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Developer Docs
          </Link>
          <div className="mt-5 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#FF5A36]">
                API Reference · OpenAPI 3.1
              </p>
              <h1 className="font-display text-4xl md:text-5xl mt-2">
                Every endpoint. One click to run.
              </h1>
              <p className="mt-4 max-w-2xl text-[15px] text-white/70">
                This reference is generated automatically from the running
                backend, so it always matches production. Use the sandbox key{" "}
                <span className="font-mono text-[13px] text-white">
                  afg_test_sandbox_docs_public_00000000
                </span>{" "}
                in the Authorize dialog to execute requests instantly.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href={OPENAPI_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="api-ref-download-json"
                className="inline-flex items-center gap-1.5 rounded-full bg-white text-[#0A1128] px-4 py-2 text-[13px] font-medium hover:bg-white/90 transition-colors"
              >
                <Download className="h-3.5 w-3.5" /> openapi.json
              </a>
              <a
                href={`${API}/api/redoc`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="api-ref-redoc"
                className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-transparent text-white px-4 py-2 text-[13px] font-medium hover:bg-white/5 transition-colors"
              >
                ReDoc view <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F7F9FC] py-8 px-4 md:px-6 lg:px-10 min-h-[70vh]">
        <div className="mx-auto max-w-6xl bg-white rounded-2xl af-shadow-card p-2 md:p-4">
          {!ready && (
            <div
              data-testid="api-ref-loading"
              className="py-24 text-center text-slate-500 text-[14px]"
            >
              Loading OpenAPI spec…
            </div>
          )}
          <div id="swagger-ui" data-testid="api-ref-swagger" />
        </div>
      </section>
    </>
  );
}
